import {
  BasePagination,
  LEVEL_TYPE,
} from "api/interfaces/commonInterface.interface";
import { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteExpandTable from "components/blocks/table/InfiniteExpandTable";
import { useInfiniteQuery } from "react-query";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import {
  SubscriptionOrgListColumnType,
  SubscriptionOrgListResult,
  SusbcriptonOrgListResponse,
} from "api/interfaces/subscription.interface";
import { getSubscriptonOrgList } from "api/subscriptionAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ColumnType, ExpandableConfig } from "antd/es/table/interface";
import { checkIsDoubleOrgTree } from "utils/AccountUtil";
import { useIsMobile } from "components/MediaQuery";
import useStompDebouncedRefetch from "hook/useStompDebouncedRefetch";

export default function SubscriptionExpandOrgList({
  selectedData,
  columns,
  queryInfo,
  expandable,
}: {
  selectedData: SubscriptionOrgListColumnType & { key?: string };
  columns: ColumnType<SubscriptionOrgListColumnType>[];
  queryInfo: BasePagination;
  expandable?: ExpandableConfig<SubscriptionOrgListColumnType>;
}) {
  const isMobile = useIsMobile();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [expandQueryInfo, setExpandQueryInfo] = useState<BasePagination>({
    ...baseQueryInfo,
    sortType: "name",
  });

  const { accountId } = selectedData;

  const isDoubleTree = useMemo(
    () =>
      checkIsDoubleOrgTree({
        accountLevel: selectedAccount.accountLevel,
        selectedLevel: selectedData.level,
        parentLevel: selectedData.parentLevel,
        rowKey: selectedData.key,
      }),
    [selectedAccount.accountLevel, selectedData]
  );

  const stableQueryKey = useQueryKey({ ...expandQueryInfo, pageNum: 0 });

  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(
    ["getSubscriptonOrgList", stableQueryKey, accountId],
    ({ pageParam = 0 }) =>
      getSubscriptonOrgList({
        payload: {
          ...expandQueryInfo,
          pageNum: pageParam,
        },
        accountId,
      }),
    {
      enabled: !!accountId,
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
      retry: 0,
      getNextPageParam: (lastPage: SusbcriptonOrgListResponse | undefined) => {
        if (!lastPage?.page) return undefined;

        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;

        if (nextPage * pageLimit < total) return nextPage;
        return undefined;
      },
    }
  );
  useStompDebouncedRefetch({
    selector: (state) =>
      ({
        organizationListUpdated: state.stomp.organizationListUpdated,
        subscriptionRequestUpdated:
          state.subscriptionStomp.subscriptionRequestUpdated,
        subscriptionConfirmUpdated:
          state.subscriptionStomp.subscriptionConfirmUpdated,
        subscriptionActivatedUpdated:
          state.subscriptionStomp.subscriptionActivatedUpdated,
      } as Record<string, unknown>),
    stompKey: [
      "organizationListUpdated",
      "subscriptionRequestUpdated",
      "subscriptionConfirmUpdated",
      "subscriptionActivatedUpdated",
    ],
    refetch: refetch,
  });

  const loadMore = useCallback(() => {
    if (!hasNextPage) return;
    if (isFetchingNextPage) return;
    if (isInitialLoading) return;

    fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isInitialLoading, fetchNextPage]);

  const list = useMemo(() => {
    const parentRowKey = String(selectedData.key ?? selectedData.accountId);

    return (data?.pages || [])
      .flatMap((page: SusbcriptonOrgListResponse) => page.result ?? [])
      .map((account: SubscriptionOrgListResult) => {
        return {
          ...account,
          key: `${parentRowKey}:${account.accountId}`,
        };
      });
  }, [data, selectedData.accountId, selectedData.key]);

  useEffect(() => {
    setExpandQueryInfo((prev) => ({
      ...prev,
      sortDirection: queryInfo.sortDirection,
      sortType: queryInfo.sortType !== "name" ? queryInfo.sortType : "name",
      pageNum: 0,
    }));
  }, [queryInfo.sortType, queryInfo.sortDirection]);

  return (
    <InfiniteExpandTable
      data={list}
      loadMore={loadMore}
      isLoading={isInitialLoading}
      hasMore={hasNextPage}
      columns={columns}
      tableWrapperHeight={0}
      leftPadding={isMobile ? 0 : 50}
      expandable={expandable}
      isDoubleTree={isDoubleTree}
    />
  );
}
